/**
 * /admin/kitchen — Founder-only daily production manifest + 4x6 label PDF download.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Printer, ArrowLeft, ChefHat, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('academy_token') || ''}` });

const KitchenAdmin = () => {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const load = () => {
    setLoading(true); setErr('');
    api.get(`/fuel/admin/manifest?date=${date}`, { headers: auth() })
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || 'Access denied'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [date]);

  const downloadPDF = async () => {
    try {
      const r = await api.get(`/fuel/admin/labels.pdf?date=${date}`, { headers: auth(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a'); a.href = url; a.download = `fuel-labels-${date}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Labels PDF downloaded');
    } catch (e) { toast.error(e?.response?.data?.detail || 'PDF download failed'); }
  };

  return (
    <div className="min-h-screen bg-[#0B0E14] text-white pt-16 pb-24 px-4" data-testid="kitchen-admin">
      <div className="container mx-auto max-w-5xl">
        <Link to="/admin" className="text-[10px] font-black uppercase tracking-[0.3em] text-zinc-500 hover:text-white flex items-center gap-1 mb-4"><ArrowLeft className="w-3 h-3" /> Admin Hub</Link>

        <div className="text-[10px] tracking-[0.5em] uppercase font-black text-emerald-400">FUEL · Kitchen Ops</div>
        <h1 className="text-3xl md:text-5xl font-black uppercase tracking-tight mt-2"><ChefHat className="w-8 h-8 inline mr-2" />Daily Manifest</h1>

        <div className="flex flex-wrap items-center gap-3 mt-6">
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            className="bg-black border-2 border-zinc-800 focus:border-emerald-500 px-3 py-2 text-sm outline-none" data-testid="kitchen-date" />
          <button onClick={downloadPDF} disabled={!data?.roster?.length}
            className="bg-emerald-500 hover:bg-emerald-400 disabled:bg-zinc-800 disabled:text-zinc-600 text-black px-5 py-2 text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
            data-testid="kitchen-print-labels"><Printer className="w-3 h-3" /> Download 4×6 Labels PDF</button>
        </div>

        {loading && <Loader2 className="w-6 h-6 animate-spin text-emerald-500 mt-8" />}
        {err && <div className="mt-6 text-red-400">{err}</div>}

        {data && (<>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mt-8">
            {[
              { label: 'Students', value: data.totals.students },
              { label: 'Lunches',  value: data.totals.lunches  },
              { label: 'Juices',   value: data.totals.juices   },
              { label: 'Waters',   value: data.totals.waters   },
              { label: 'Shakes',   value: data.totals.shakes   },
              { label: 'Snacks',   value: data.totals.snacks   },
            ].map((s) => (
              <div key={s.label} className="border-2 border-zinc-900 bg-zinc-950 p-4" data-testid={`total-${s.label.toLowerCase()}`}>
                <div className="text-[9px] uppercase tracking-widest font-black text-zinc-500">{s.label}</div>
                <div className="text-3xl font-black text-emerald-400 mt-1">{s.value}</div>
              </div>
            ))}
          </div>

          <div className="mt-10 border-2 border-zinc-900 bg-zinc-950 overflow-hidden">
            <div className="px-4 py-3 border-b-2 border-zinc-900 text-[10px] uppercase tracking-widest font-black text-zinc-500">Roster · {date}</div>
            {(data.roster || []).length === 0 ? (
              <div className="p-6 text-sm text-zinc-500">No paid orders for this date yet.</div>
            ) : (
              (data.roster || []).map((r) => (
                <div key={r.order_id} className="flex flex-wrap items-start gap-3 px-4 py-3 border-b border-zinc-900 last:border-b-0" data-testid={`roster-${r.order_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-white">{r.student_name}</div>
                    <div className="text-[11px] text-zinc-500">{r.parent_email} · {r.phone}</div>
                  </div>
                  {r.allergies && r.allergies.toLowerCase() !== 'none' && (
                    <div className="px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-white" style={{ backgroundColor: '#FF0000' }} data-testid={`allergy-${r.order_id}`}>
                      <AlertTriangle className="w-3 h-3 inline mr-1" /> Allergies · {r.allergies}
                    </div>
                  )}
                  {r.dislikes && (
                    <div className="px-2.5 py-1 text-[10px] font-black uppercase tracking-widest text-white" style={{ backgroundColor: '#FF9800' }} data-testid={`dislike-${r.order_id}`}>
                      Prefs · {r.dislikes}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </>)}
      </div>
    </div>
  );
};

export default KitchenAdmin;
